import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { haptic } from '../haptics';
import { openLayer } from '../backStack';
import { lockBody, unlockBody } from './bodyLock';

/**
 * A phone bottom sheet: slides up from the bottom edge, dims the page, and closes on a
 * downward swipe (on the grab bar, or anywhere on the content once it is scrolled to its
 * top), a tap on the backdrop, the × button, Escape, or the phone's Back.
 *
 * Rendered in its own portal with the page's `dir` — NOT through PortalOverlay, which forces
 * `direction: ltr` on modals and would put Arabic content in the wrong order.
 */
export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  /** Sticky area under the scrolling body — the sheet's main actions live here. */
  footer?: React.ReactNode;
  /** Take nearly the whole screen (long forms, menus). */
  full?: boolean;
  /** Accessible name when there is no visible title. */
  ariaLabel?: string;
  className?: string;
}

const CLOSE_MS = 240;

const BottomSheet: React.FC<BottomSheetProps> = ({
  open, onClose, title, subtitle, children, footer, full, ariaLabel, className,
}) => {
  const { i18n, t } = useTranslation();
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ y0: number; t0: number; dy: number } | null>(null);
  const returnFocus = useRef<Element | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Mount → next frame add `is-open` so the slide-in transitions; close → wait for the slide-out.
  useEffect(() => {
    if (open) {
      setMounted(true);
      returnFocus.current = document.activeElement;
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    const tm = window.setTimeout(() => setMounted(false), CLOSE_MS);
    return () => window.clearTimeout(tm);
  }, [open]);

  useLayoutEffect(() => {
    if (!mounted) return;
    lockBody();
    return () => unlockBody();
  }, [mounted]);

  // The phone's Back closes the sheet instead of leaving the page.
  useLayoutEffect(() => {
    if (!open) return;
    return openLayer(() => onCloseRef.current());
  }, [open]);

  useEffect(() => {
    if (!shown) return;
    sheetRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const el = returnFocus.current as HTMLElement | null;
      if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
    };
  }, [shown]);

  const settle = useCallback(() => {
    const d = drag.current;
    drag.current = null;
    setDragging(false);
    if (!d) return;
    const h = sheetRef.current?.offsetHeight || 1;
    const velocity = d.dy / Math.max(1, performance.now() - d.t0); // px per ms
    if (d.dy > h * 0.28 || (d.dy > 24 && velocity > 0.55)) {
      haptic.tick();
      onCloseRef.current();
    }
    setDragY(0);
  }, []);

  /* Drag from the grab bar / header (pointer events: works for touch and for a mouse). */
  const onGrabDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
    drag.current = { y0: e.clientY, t0: performance.now(), dy: 0 };
    setDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onGrabMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    d.dy = Math.max(0, e.clientY - d.y0);
    setDragY(d.dy);
  };

  /* Drag from the content, but only while it is scrolled to the very top (iOS sheet feel).
     Touch events with passive:false — the only way to stop the page from scrolling instead. */
  useEffect(() => {
    const body = bodyRef.current;
    if (!body || !shown) return;
    let y0 = 0;
    let x0 = 0;
    let active = false;
    const start = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      y0 = e.touches[0].clientY;
      x0 = e.touches[0].clientX;
      active = body.scrollTop <= 0;
    };
    const move = (e: TouchEvent) => {
      if (!active) return;
      const dy = e.touches[0].clientY - y0;
      const dx = e.touches[0].clientX - x0;
      if (!drag.current) {
        if (dy <= 6 || Math.abs(dx) > dy) {
          if (dy < -6 || Math.abs(dx) > 10) active = false; // scrolling up or sideways — not ours
          return;
        }
        drag.current = { y0, t0: performance.now(), dy: 0 };
        setDragging(true);
      }
      e.preventDefault();
      drag.current.dy = Math.max(0, dy);
      setDragY(drag.current.dy);
    };
    const end = () => {
      active = false;
      if (drag.current) settle();
    };
    body.addEventListener('touchstart', start, { passive: true });
    body.addEventListener('touchmove', move, { passive: false });
    body.addEventListener('touchend', end);
    body.addEventListener('touchcancel', end);
    return () => {
      body.removeEventListener('touchstart', start);
      body.removeEventListener('touchmove', move);
      body.removeEventListener('touchend', end);
      body.removeEventListener('touchcancel', end);
    };
  }, [shown, settle]);

  if (!mounted) return null;

  return createPortal(
    <div className={`m-sheet-root${shown ? ' is-open' : ''}`} dir={i18n.dir()}>
      <div className="m-sheet-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        ref={sheetRef}
        className={`m-sheet${full ? ' m-sheet--full' : ''}${dragging ? ' is-dragging' : ''}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || (typeof title === 'string' ? title : undefined)}
        tabIndex={-1}
        style={dragY ? { transform: `translate3d(0, ${dragY}px, 0)` } : undefined}
      >
        <div
          className="m-sheet-grab"
          onPointerDown={onGrabDown}
          onPointerMove={onGrabMove}
          onPointerUp={settle}
          onPointerCancel={settle}
        >
          <span className="m-sheet-handle" aria-hidden="true" />
          {(title || subtitle) && (
            <div className="m-sheet-head">
              <div className="m-sheet-titles">
                {title && <div className="m-sheet-title">{title}</div>}
                {subtitle && <div className="m-sheet-sub">{subtitle}</div>}
              </div>
              <button type="button" className="m-icon-btn m-sheet-x" onClick={onClose} aria-label={t('common.close', 'Close')}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
              </button>
            </div>
          )}
        </div>
        <div className="m-sheet-body" ref={bodyRef}>{children}</div>
        {footer && <div className="m-sheet-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};

export default BottomSheet;
